import {
  DEFAULT_SIGN_OUT_SCOPE,
  getBrowserSupabaseClient,
  toGoTrueSignOutScope,
  type SignOutScope,
} from "@wtc/supabase";
import { debug } from "@/lib/utils/debug";
import { clearSupabaseCookies, clearAuthCookies } from "@/lib/utils/auth/clear-cookies";
import { clearCachedAuthState } from "@wtc/ui";
import { clearAllCachedUsers } from "@/lib/auth/core/auth-cache";

interface StorageOptions {
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
}

interface CookieOptions {
  clearCookies?: boolean;
  clearAllAuthCookies?: boolean;
}

function safeRun(label: string, fn: () => void): void {
  try {
    fn();
    debug.auth(`${label} cleared`);
  } catch (error) {
    debug.auth(`Error clearing ${label}:`, error);
  }
}

/**
 * Core Supabase sign-out call. Returns success flag + error message.
 *
 * The scope is ALWAYS passed explicitly: auth-js defaults to `'global'`, which
 * would end every session this user has on every device and kill their MCP
 * connections. See the SignOutScope contract in `@wtc/supabase`.
 */
export async function performSupabaseSignOut(
  scope: SignOutScope = DEFAULT_SIGN_OUT_SCOPE
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const supabase = getBrowserSupabaseClient();
    const { error } = await supabase.auth.signOut({ scope: toGoTrueSignOutScope(scope) });

    if (error) {
      debug.auth("Supabase sign out error:", error);
      return { success: false, error: error.message };
    }

    debug.auth("Supabase sign out successful");
    return { success: true };
  } catch (error) {
    debug.auth("Unexpected error during Supabase sign out:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Always-on auth cache clear plus optional local/session storage wipes.
 */
export function clearBrowserStorage(options: StorageOptions): void {
  try {
    clearCachedAuthState();
    clearAllCachedUsers();
    debug.auth("Auth caches cleared");
  } catch (error) {
    debug.auth("Error clearing auth caches:", error);
  }

  if (options.clearLocalStorage) {
    safeRun("Local storage", () => localStorage.clear());
  }
  if (options.clearSessionStorage) {
    safeRun("Session storage", () => sessionStorage.clear());
  }
}

/**
 * Clear either all auth cookies or just Supabase cookies, depending on flags.
 */
export function clearBrowserCookies(options: CookieOptions): void {
  if (options.clearAllAuthCookies) {
    try {
      const result = clearAuthCookies();
      debug.auth("All auth cookies cleared", result);
    } catch (error) {
      debug.auth("Error clearing all auth cookies:", error);
    }
  } else if (options.clearCookies) {
    try {
      const result = clearSupabaseCookies();
      debug.auth("Supabase cookies cleared", result);
    } catch (error) {
      debug.auth("Error clearing Supabase cookies:", error);
    }
  }
}

/**
 * Hard navigation fallback used when no Next.js router is available.
 * Uses a small setTimeout for Chrome compatibility after cookie clear.
 */
export function navigateAfterSignOut(redirectTo?: string): void {
  const targetUrl = redirectTo || "/";
  try {
    setTimeout(() => {
      window.location.href = targetUrl;
      debug.auth(`Navigated to: ${targetUrl}`);
    }, 100);
  } catch (error) {
    debug.auth("Error during navigation:", error);
    setTimeout(() => {
      window.location.href = "/";
    }, 100);
  }
}
